/**
 * ThemeQuickSwitch
 * Light / Dark / System picker for the sidebar user menu.
 *
 * Sized to sit in a menu: one row, label left, an icon-only pill right — the
 * same height as the menu items around it. Same three values as the Appearance
 * settings selector, reachable in one click from anywhere in the app.
 *
 * Plain buttons rather than DropdownMenuItems, so picking a theme doesn't close
 * the menu — the point is to see the change and keep adjusting.
 */

import { Monitor, Moon, Sun } from "lucide-react"
import { useTheme } from "@/contexts/theme-context"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { cn } from "@/lib/utils"

type Theme = "light" | "dark" | "system"

const THEME_OPTIONS: { value: Theme; label: string; icon: typeof Sun }[] = [
  { value: "light", label: "Light", icon: Sun },
  { value: "dark", label: "Dark", icon: Moon },
  { value: "system", label: "System", icon: Monitor },
]

export function ThemeQuickSwitch() {
  const { theme, setTheme } = useTheme()

  return (
    <div className="flex items-center justify-between gap-2 px-2 py-1.5">
      <span className="type-control text-muted-foreground">Theme</span>
      <div
        role="radiogroup"
        aria-label="Theme"
        className="flex items-center gap-0.5 rounded-md bg-muted p-0.5"
      >
        {THEME_OPTIONS.map(({ value, label, icon: Icon }) => {
          const selected = theme === value
          return (
            <Tooltip key={value}>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  aria-label={label}
                  onClick={() => setTheme(value)}
                  data-testid={`theme-${value}`}
                  className={cn(
                    "flex h-6 w-6 items-center justify-center rounded transition-colors",
                    "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
                    selected
                      ? "bg-background text-foreground shadow-sm"
                      : "text-muted-foreground hover:text-foreground"
                  )}
                >
                  <Icon className="h-3.5 w-3.5" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="top">{label}</TooltipContent>
            </Tooltip>
          )
        })}
      </div>
    </div>
  )
}
